import { useEffect, useState, useRef } from "react";
import { BASE_URLS } from "../components/env";
import { getAnalyticsCached, setAnalyticsCached } from "../utils/analyticsCache";

function _backendUrl() {
  const override = process.env.REACT_APP_BACKEND_URL;
  if (override && override.trim() !== "") return override;
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1"
    ? BASE_URLS.local.backend
    : BASE_URLS.render.backend;
}

function _windowToRange(timeWindow) {
  const days = { "24h": 1, "7d": 7, "30d": 30, "90d": 90, all: 3650 }[timeWindow] || 7;
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const end = fmt(new Date());
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - (days - 1));
  const start = fmt(startDate);
  return { start, end };
}

async function _headers() {
  const mod = await import("../components/supabaseClient");
  const { data } = await mod.supabasePublic().auth.getSession();
  const token = data?.session?.access_token;
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "X-Schema": mod.supabaseSchema || "public",
  };
}

export function useAnalyticsQuery({ tab, timeWindow, userId = null, scope = null, enabled = true, refreshNonce = 0, bucket = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const reqId = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    reqId.current += 1;
    const myReq = reqId.current;

    const baseCacheKey = scope ? `${tab}::${scope}` : tab;
    const cacheKey = (tab === "individual" && bucket) ? `${baseCacheKey}::b=${bucket}` : baseCacheKey;
    const cached = getAnalyticsCached(cacheKey, timeWindow, userId);
    if (cached) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { start, end } = _windowToRange(timeWindow);
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
        const scopeQS = scope ? `&scope=${encodeURIComponent(scope)}` : "";
        const bucketQS = (tab === "individual" && bucket) ? `&bucket=${encodeURIComponent(bucket)}` : "";
        const path = tab === "individual"
          ? `/api/analytics/user-stats/?user_id=${encodeURIComponent(userId)}&start=${start}&end=${end}&tz=${encodeURIComponent(tz)}${scopeQS}${bucketQS}`
          : `/api/analytics/aggregate-stats/?start=${start}&end=${end}&tz=${encodeURIComponent(tz)}${scopeQS}`;
        const headers = await _headers();
        const res = await fetch(`${_backendUrl()}${path}`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (reqId.current !== myReq) return;
        setAnalyticsCached(cacheKey, timeWindow, userId, json);
        setData(json);
      } catch (e) {
        if (reqId.current !== myReq) return;
        setError(e);
      } finally {
        if (reqId.current === myReq) setLoading(false);
      }
    })();
  }, [tab, timeWindow, userId, scope, enabled, refreshNonce, bucket]);

  return { data, loading, error };
}
